import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  partnerKeys,
  partnerMutations,
  partnerQueries,
} from '../../api/partnerQueries';
import { seedListCache } from '../../api/seedListCache';

export function usePartners() {
  return useQuery(partnerQueries.list());
}

/* one partner incl. its role-dependent embedded details */
export function usePartner(uuid: string | undefined) {
  return useQuery({ ...partnerQueries.single(uuid), enabled: Boolean(uuid) });
}

/*
 * A written partner shows up in two places: in the list the sidebar
 * selector and the master table read, and in the single-partner query
 * behind the details. The two keys are deliberately not nested into
 * one another (see partnerQueries), so both are named here.
 */
function useInvalidatePartners() {
  const queryClient = useQueryClient();

  return async (uuid?: string) => {
    if (uuid) {
      await queryClient.invalidateQueries({
        queryKey: partnerKeys.single(uuid),
      });
    }
    await queryClient.invalidateQueries({ queryKey: partnerKeys.all });
  };
}

export function useCreatePartner() {
  const queryClient = useQueryClient();
  const invalidatePartners = useInvalidatePartners();

  const createPartnerMutation = useMutation({
    ...partnerMutations.post(),
    onSuccess: (created) => {
      /* into the cached list right away, so the partner page (and the
         sidebar selector) hold the new partner the moment the create
         form hands over (see seedListCache) */
      seedListCache(queryClient, partnerKeys.list(), created);
      return invalidatePartners(created?.uuid);
    },
  });

  return {
    createPartnerAsync: createPartnerMutation.mutateAsync,
    isPending: createPartnerMutation.isPending,
    isError: createPartnerMutation.isError,
    error: createPartnerMutation.error,
  };
}

export function useUpdatePartner(uuid: string | undefined) {
  const invalidatePartners = useInvalidatePartners();

  const updatePartnerMutation = useMutation({
    ...partnerMutations.patch(uuid),
    onSuccess: () => invalidatePartners(uuid),
  });

  return {
    updatePartnerAsync: updatePartnerMutation.mutateAsync,
    isPending: updatePartnerMutation.isPending,
    isError: updatePartnerMutation.isError,
    error: updatePartnerMutation.error,
  };
}

export function useDeletePartner() {
  const invalidatePartners = useInvalidatePartners();

  const deletePartnerMutation = useMutation({
    ...partnerMutations.delete(),
    onSuccess: (_data, uuid) => invalidatePartners(uuid),
  });

  return {
    deletePartnerAsync: deletePartnerMutation.mutateAsync,
    isPending: deletePartnerMutation.isPending,
    isError: deletePartnerMutation.isError,
    error: deletePartnerMutation.error,
  };
}
